import React, { useEffect, useMemo, useState } from 'react';
import { App as AntApp, Button, Card, Col, DatePicker, Descriptions, Drawer, Input, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import { DownloadOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../utils/api';
import './UserAuditPanel.css';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const actionLabels = {
  'auth.login_success': 'เข้าสู่ระบบสำเร็จ',
  'auth.login_failed': 'เข้าสู่ระบบไม่สำเร็จ',
  'auth.refresh_failed': 'ต่ออายุ Session ไม่สำเร็จ',
  'security.access_denied': 'ถูกปฏิเสธการเข้าถึง',
  page_view: 'เปิดหน้า',
  'user.create': 'สร้างผู้ใช้',
  'user.update': 'แก้ไขผู้ใช้',
  'user.password_reset': 'เปลี่ยนรหัสผ่าน',
  'user.activate': 'เปิดใช้งานผู้ใช้',
  'user.deactivate': 'ปิดใช้งานผู้ใช้',
  'access.menu_update': 'แก้ไขการตั้งค่าเมนู',
  'access.role_create': 'สร้างบทบาท',
  'access.role_update': 'แก้ไขบทบาท/สิทธิ์',
  'access.role_clone': 'คัดลอกบทบาท',
  'access.role_deactivate': 'ปิดใช้งานบทบาท',
};

const categoryLabels = { read: 'อ่านข้อมูล', mutation: 'เปลี่ยนแปลงข้อมูล', security: 'ความปลอดภัย', navigation: 'การเปิดหน้า', system: 'ระบบ' };
const formatDate = value => value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-';
const renderJson = value => {
  if (!value) return <Text type="secondary">ไม่มีข้อมูล</Text>;
  let parsed = value;
  if (typeof value === 'string') { try { parsed = JSON.parse(value); } catch (_) { parsed = value; } }
  return <pre className="user-audit-json">{JSON.stringify(parsed, null, 2)}</pre>;
};

export default function UserAuditPanel({ initialActorUserId, onInitialActorConsumed }) {
  const { message } = AntApp.useApp();
  const [range, setRange] = useState([dayjs().startOf('month'), dayjs().endOf('day')]);
  const [filters, setFilters] = useState({ actor_user_id: undefined, category: undefined, success: undefined, search: '', ip_address: '' });
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25, total: 0 });
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const params = page => ({
    page: page || pagination.current,
    limit: pagination.pageSize,
    from_date: range?.[0]?.format('YYYY-MM-DD'),
    to_date: range?.[1]?.format('YYYY-MM-DD'),
    ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined && value !== '')),
  });

  const load = async page => {
    setLoading(true);
    try {
      const [listResponse, summaryResponse] = await Promise.all([
        api.get('/api/user-audit', { params: params(page) }),
        api.get('/api/user-audit/summary', { params: params(page) }),
      ]);
      const list = listResponse.data?.data || [];
      const pageInfo = listResponse.data?.pagination || {};
      setRows(list);
      setSummary(summaryResponse.data?.data || {});
      setPagination(current => ({ ...current, current: Number(pageInfo.page || page || current.current), total: Number(pageInfo.total || 0) }));
    } catch (error) {
      message.error(error.response?.data?.message || 'ไม่สามารถโหลดประวัติผู้ใช้งานได้');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(1); }, [range, filters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/api/users', { params: { include_inactive: true, limit: 1000 } })
      .then(response => setUsers(response.data?.data || []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!initialActorUserId) return;
    setFilters(current => ({ ...current, actor_user_id: Number(initialActorUserId) }));
    onInitialActorConsumed?.();
  }, [initialActorUserId, onInitialActorConsumed]);

  const openDetail = async record => {
    try {
      const response = await api.get(`/api/user-audit/${record.id}`);
      setSelected(response.data?.data || record);
      setDrawerOpen(true);
    } catch (error) { message.error('ไม่สามารถเปิดรายละเอียดกิจกรรมได้'); }
  };

  const exportCsv = async () => {
    try {
      const response = await api.get('/api/user-audit/export', { params: params(1), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url; link.download = `user-audit-${dayjs().format('YYYYMMDD-HHmm')}.csv`; link.click();
      URL.revokeObjectURL(url);
    } catch (error) { message.error(error.response?.data?.message || 'ส่งออกข้อมูลไม่สำเร็จ'); }
  };

  const userOptions = useMemo(() => users.map(user => ({ value: user.id, label: `${user.name} (${user.email})` })), [users]);
  const columns = [
    { title: 'เวลา', dataIndex: 'occurred_at', width: 170, render: formatDate },
    { title: 'ผู้ดำเนินการ', key: 'actor', width: 180, render: (_, row) => row.actor_name || row.actor_name_snapshot || (row.actor_user_id ? `ผู้ใช้ #${row.actor_user_id}` : 'SYSTEM') },
    { title: 'กิจกรรม', dataIndex: 'action', width: 190, render: value => <Text>{actionLabels[value] || value}</Text> },
    { title: 'หมวด', dataIndex: 'category', width: 130, render: value => <Tag color={value === 'security' ? 'red' : value === 'mutation' ? 'blue' : 'default'}>{categoryLabels[value] || value}</Tag> },
    { title: 'รายการอ้างอิง', key: 'resource', render: (_, row) => <Space direction="vertical" size={0}><Text>{row.resource_type || '-' }{row.resource_id ? ` #${row.resource_id}` : ''}</Text>{row.document_number && <Text type="secondary">{row.document_number}</Text>}</Space> },
    { title: 'ผลลัพธ์', dataIndex: 'is_success', width: 105, render: value => <Tag color={Number(value) ? 'success' : 'error'}>{Number(value) ? 'สำเร็จ' : 'ไม่สำเร็จ'}</Tag> },
    { title: 'IP', dataIndex: 'ip_address', width: 130, render: value => value || '-' },
  ];

  return <div className="user-audit-panel">
    <Card className="user-audit-hero" bordered={false}>
      <Space align="start"><SafetyCertificateOutlined className="user-audit-hero__icon" /><div><Typography.Title level={3}>ประวัติผู้ใช้งาน</Typography.Title><Text type="secondary">บันทึกกิจกรรม การเข้าถึง และเหตุการณ์ความปลอดภัยย้อนหลัง 1 ปี — ดูได้เฉพาะ Super Admin</Text></div></Space>
      <Row gutter={[12, 12]} className="user-audit-stats">
        <Col xs={12} md={6}><Statistic title="กิจกรรมวันนี้" value={summary.activity_today || 0} /></Col>
        <Col xs={12} md={6}><Statistic title="ผู้ใช้งานที่เปิดใช้" value={summary.active_users || 0} valueStyle={{ color: '#1677ff' }} /></Col>
        <Col xs={12} md={6}><Statistic title="เข้าสู่ระบบไม่สำเร็จ" value={summary.failed_logins || 0} valueStyle={{ color: '#cf1322' }} /></Col>
        <Col xs={12} md={6}><Statistic title="ถูกปฏิเสธสิทธิ์" value={summary.access_denied || 0} valueStyle={{ color: '#d46b08' }} /></Col>
        <Col xs={24} md={6}><Statistic title="กิจกรรมเสี่ยง" value={summary.risky_actions || 0} valueStyle={{ color: '#722ed1' }} /></Col>
      </Row>
    </Card>
    <Card bordered={false} className="user-audit-table-card" extra={<Space wrap><Button icon={<ReloadOutlined />} onClick={() => load(1)}>รีเฟรช</Button><Button icon={<DownloadOutlined />} onClick={exportCsv}>ส่งออก CSV</Button></Space>}>
      <Row gutter={[10, 10]} className="user-audit-filters">
        <Col xs={24} lg={7}><RangePicker value={range} onChange={setRange} format="DD/MM/YYYY" style={{ width: '100%' }} /></Col>
        <Col xs={24} sm={12} lg={5}><Select allowClear showSearch optionFilterProp="label" placeholder="ผู้ดำเนินการ" value={filters.actor_user_id} onChange={value => setFilters(current => ({ ...current, actor_user_id: value }))} options={userOptions} style={{ width: '100%' }} /></Col>
        <Col xs={12} sm={6} lg={4}><Select allowClear placeholder="ประเภท" value={filters.category} onChange={value => setFilters(current => ({ ...current, category: value }))} options={Object.entries(categoryLabels).map(([value, label]) => ({ value, label }))} style={{ width: '100%' }} /></Col>
        <Col xs={12} sm={6} lg={3}><Select allowClear placeholder="ผลลัพธ์" value={filters.success} onChange={value => setFilters(current => ({ ...current, success: value }))} options={[{ value: 'true', label: 'สำเร็จ' }, { value: 'false', label: 'ไม่สำเร็จ' }]} style={{ width: '100%' }} /></Col>
        <Col xs={24} lg={5}><Input.Search allowClear placeholder="ค้นหากิจกรรม, เอกสาร, path" value={filters.search} onChange={event => setFilters(current => ({ ...current, search: event.target.value }))} onSearch={() => load(1)} /></Col>
      </Row>
      <Table rowKey="id" loading={loading} columns={columns} dataSource={rows} scroll={{ x: 1050 }} onRow={record => ({ onClick: () => openDetail(record) })} pagination={{ ...pagination, showSizeChanger: true, showTotal: total => `ทั้งหมด ${total} รายการ`, onChange: (page, pageSize) => { setPagination(current => ({ ...current, current: page, pageSize })); load(page); } }} />
    </Card>
    <Drawer title="รายละเอียดกิจกรรม" width={620} open={drawerOpen} onClose={() => setDrawerOpen(false)}>
      {selected && <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="เวลา">{formatDate(selected.occurred_at)}</Descriptions.Item>
        <Descriptions.Item label="ผู้ดำเนินการ">{selected.actor_name || selected.actor_name_snapshot || 'SYSTEM'}</Descriptions.Item>
        <Descriptions.Item label="กิจกรรม">{actionLabels[selected.action] || selected.action}</Descriptions.Item>
        <Descriptions.Item label="ผลลัพธ์"><Tag color={Number(selected.is_success) ? 'success' : 'error'}>{Number(selected.is_success) ? 'สำเร็จ' : 'ไม่สำเร็จ'}</Tag></Descriptions.Item>
        <Descriptions.Item label="ปลายทาง">{selected.http_method || '-'} {selected.request_path || '-'}</Descriptions.Item>
        <Descriptions.Item label="อุปกรณ์ / IP">{selected.ip_address || '-'}<br /><Text type="secondary">{selected.user_agent || '-'}</Text></Descriptions.Item>
        <Descriptions.Item label="ก่อนเปลี่ยนแปลง">{renderJson(selected.before_data)}</Descriptions.Item>
        <Descriptions.Item label="หลังเปลี่ยนแปลง">{renderJson(selected.after_data)}</Descriptions.Item>
        <Descriptions.Item label="ข้อมูลประกอบ">{renderJson(selected.metadata)}</Descriptions.Item>
      </Descriptions>}
    </Drawer>
  </div>;
}
